import React, {useCallback} from 'react'
import {useDispatch} from 'react-redux'
import {ContentHotel} from '@findhotel/sapi'
import {
  setLightboxOverlayVisibility,
  setLightboxSelectedImage
} from 'modules/accommodation/slice'
import {isEmpty} from 'ramda'
import {imageProvider} from 'utils/imageProvider'

import {Icon} from '@vio/atlas/Icon'
import {Image} from '@vio/atlas/Image'
import {useDispatchTeamEvent} from '@vio/core/src/analytics/components/TeamContext'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import {getGalleryImageAlt} from '../utils/getGalleryImageAlt'
import {GridGalleryLoading} from './GridGalleryLoading'
import {getImageSize} from './helpers'
import {AllPhotosButton, Container, Wrapper, WrapperItem} from './styles'

export const BASE_NUM_IMAGES = 5

interface Props {
  images: ContentHotel['images']
  hotelName: string
  isLoading?: boolean
}

export const GridGallery: React.FC<Props> = ({
  images,
  hotelName,
  isLoading = false
}) => {
  const dispatch = useDispatch()
  const dispatchTeamEvent = useDispatchTeamEvent()
  const imagesToRender = images?.slice(0, BASE_NUM_IMAGES) ?? []
  const numImagesInGrid = imagesToRender.length
  const numTotalImages = images?.length

  const handleImageClick = useCallback(
    (selectedImage?: string) => {
      dispatchTeamEvent(
        trackEvent({
          category: Category.User,
          entity: Entity.HotelDetailsGallery,
          action: Action.Clicked
        })
      )
      dispatch(setLightboxSelectedImage(selectedImage))
      dispatch(setLightboxOverlayVisibility(true))
    },
    [dispatch, dispatchTeamEvent]
  )

  if (isLoading) return <GridGalleryLoading />

  if (isEmpty(images)) return null

  return (
    <Container data-testid="grid-gallery">
      <Wrapper
        className={`--${numImagesInGrid}-images`}
        height={360}
        mainColumnWidth="50%"
      >
        {imagesToRender.map((image, index) => (
          <WrapperItem
            key={index}
            data-id={`gallery-${index}`}
            role="button"
            onClick={() => handleImageClick(image.href)}
          >
            <Image
              alt={getGalleryImageAlt(hotelName, image, index)}
              loading={index === 0 ? 'eager' : 'lazy'}
              fetchPriority={index === 0 ? 'high' : undefined}
              url={image.href}
              imageProvider={imageProvider}
              size={getImageSize(numImagesInGrid, index)}
              isResponsive
            />
          </WrapperItem>
        ))}
      </Wrapper>

      <AllPhotosButton
        variant="tertiary"
        iconStart={<Icon name="Photo" />}
        onClick={() => handleImageClick(imagesToRender[0]?.href)}
      >
        <FormattedMessageWrapper
          defaultMessage="All photos ({numTotalImages})"
          id="allPhotosWithNumber"
          values={{
            numTotalImages
          }}
        />
      </AllPhotosButton>
    </Container>
  )
}
